import React from 'react';
import { useI18n } from '@/i18n/I18nProvider';
import { Card, CardContent } from '@/components/ui/card';
import Spinner from '@/components/ui/spinner';
import {
  CalendarBlankIcon,
  CheckCircleIcon,
  FileDashedIcon,
  FilesIcon,
  FileTextIcon,
  HourglassMediumIcon,
  WarningCircleIcon,
} from '@/components/icons';
import { formatEmiratesId } from '@/lib/formatters';
import { useGetApplications, type ApplicationData } from '@/lib/hooks/useGetApplications';
import { DocumentUpdateAppStatus } from './components/DocumentUpdateAppStatus';
import { DataUpdateAppStatus } from './components/DataUpdateAppStatus';

export default function MyApplicationsPage() {
  const { t, locale } = useI18n();
  const { applications, isLoading, error } = useGetApplications();

  // Get localized application type text
  const getApplicationTypeText = (type?: string): string => {
    switch (type) {
      case 'DATA_UPDATE':
        return t.dataUpdateApplication;
      case 'DOCUMENT_UPDATE':
        return t.documentUpdateApplication;
      default:
        return t.application;
    }
  };

  // Render application status badge component by type
  const renderAppStatus = (type?: string, statusId?: number) => {
    if (statusId === undefined) return null;
    switch (type) {
      case 'DOCUMENT_UPDATE':
        return <DocumentUpdateAppStatus statusId={statusId} />;
      case 'DATA_UPDATE':
        return <DataUpdateAppStatus statusId={statusId} />;
      default:
        return null;
    }
  };

  if (isLoading) {
    return <Spinner />;
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Card className="border-destructive">
          <CardContent className="p-6">
            <div className="text-center">
              <WarningCircleIcon className="w-16 h-16 text-destructive mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-destructive mb-2">{t.errorOccurred}</h3>
              <p className="text-muted-foreground">
                {error.title || error.message || error.error || t.failedToLoadData}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="">
      {/* Hero Header */}
      <div className="mb-2">
        <div className="py-2 md:py-3">
          <div className={`flex items-center gap-3 ${locale === 'ar' ? 'flex-row' : ''}`}>
            <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
              <FilesIcon className="w-6 h-6 text-primary" />
            </div>
            <div className={locale === 'ar' ? 'text-right' : 'text-left'}>
              <h1 className="text-xl sm:text-2xl font-bold text-foreground">{t.myApplicationsTitle}</h1>
              <p className="text-sm text-muted-foreground mb-0">{t.allChildrenApplications}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="">
        {/* Statistics Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-8">
          <Card className="border bg-slate-600 text-white shadow">
            <CardContent className="p-6">
              <div className={`flex items-center justify-between ${locale === 'ar' ? 'flex-row-reverse' : ''}`}>
                <div className={locale === 'ar' ? 'text-right' : 'text-left'}>
                  <p className="text-sm opacity-90 mb-1">{t.totalApplications}</p>
                  <p className="text-3xl font-bold">{applications.length}</p>
                </div>
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                  <FileTextIcon className="w-6 h-6" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border bg-aegold-600 text-white shadow">
            <CardContent className="p-6">
              <div className={`flex items-center justify-between ${locale === 'ar' ? 'flex-row-reverse' : ''}`}>
                <div className={locale === 'ar' ? 'text-right' : 'text-left'}>
                  <p className="text-sm opacity-90 mb-1">{t.inProgress}</p>
                  <p className="text-3xl font-bold">{applications?.filter((app) => app.status_ID === 1).length}</p>
                </div>
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                  <HourglassMediumIcon className="w-6 h-6" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border bg-aegreen-600 text-white shadow">
            <CardContent className="p-6">
              <div className={`flex items-center justify-between ${locale === 'ar' ? 'flex-row-reverse' : ''}`}>
                <div className={locale === 'ar' ? 'text-right' : 'text-left'}>
                  <p className="text-sm opacity-90 mb-1">{t.accepted}</p>
                  <p className="text-3xl font-bold">
                    {
                      applications?.filter((app) =>
                        app.applicationType === 'DOCUMENT_UPDATE' ? app.status_ID === 3 : app.status_ID === 4
                      ).length
                    }
                  </p>
                </div>
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                  <CheckCircleIcon className="w-6 h-6" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Applications List on Flat Surface */}
        {applications.length === 0 ? (
          <div className="flex flex-col items-center gap-4 py-12 text-center rounded-xl border bg-card p-6 shadow-sm">
            <div className="w-20 h-20 bg-muted rounded-full flex items-center justify-center">
              <FileDashedIcon className="w-10 h-10 text-muted-foreground" />
            </div>
            <div>
              <p className="text-lg font-semibold text-foreground mb-1">{t.noApplicationsFound}</p>
              <p className="text-sm text-muted-foreground">{t.noApplicationsRegistered}</p>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {applications.map((app: ApplicationData, index: number) => {
              const arabicName = [app.student.firstNameArabic, app.student.middleNameArabic, app.student.lastNameArabic]
                .filter(Boolean)
                .join(' ');
              const englishName = [
                app.student.firstNameEnglish,
                app.student.middleNameEnglish,
                app.student.thirdNameEnglish,
                app.student.fourthNameEnglish,
                app.student.familyNameEnglish,
              ]
                .filter(Boolean)
                .join(' ');

              const displayName =
                (locale === 'ar' ? arabicName : englishName) ||
                app.student.studentNumber ||
                app.student.source_ID ||
                `Student ${index + 1}`;

              const studentEid = app.student.emiratesId;
              const formattedDate = app.createdAt
                ? new Date(app.createdAt).toLocaleDateString(locale === 'ar' ? 'ar-AE' : 'en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                  })
                : '-';

              return (
                <div
                  key={app.id || index}
                  className="group relative rounded-xl border bg-card p-4 sm:p-5 hover:border-primary/40 hover:shadow-sm transition-all duration-200"
                >
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    {/* Left Info: Avatar + Details */}
                    <div className="flex items-start sm:items-center gap-3 min-w-0">
                      <div className="w-12 h-12 rounded-full bg-primary/10 text-primary font-bold text-lg flex items-center justify-center flex-shrink-0">
                        {displayName.charAt(0).toUpperCase()}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium mb-1">
                          {getApplicationTypeText(app.applicationType)} #{app.id}
                        </div>
                        <h3 className="font-semibold text-base sm:text-lg text-foreground group-hover:text-primary transition-colors mb-1.5">
                          {displayName}
                        </h3>
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-sm text-muted-foreground">
                          {app.student.studentNumber && (
                            <span className="flex items-center gap-1">
                              <span>{t.studentNumber}:</span>
                              <span className="font-mono font-medium">{app.student.studentNumber}</span>
                            </span>
                          )}
                          {studentEid && (
                            <span className="flex items-center gap-1">
                              <span>{t.eid}:</span>
                              <span className="font-mono font-medium">{formatEmiratesId(studentEid)}</span>
                            </span>
                          )}
                          <span className="flex items-center gap-1">
                            <CalendarBlankIcon className="w-3.5 h-3.5" />
                            <span>{formattedDate}</span>
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Right Info: Status + Actions */}
                    <div className="flex items-center justify-between sm:justify-end gap-3 pt-3 sm:pt-0 border-t sm:border-t-0 border-border">
                      {renderAppStatus(app.applicationType, app.status_ID)}

                      {app.status_ID === 2 && (
                        <a
                          href={
                            app.applicationType === 'DOCUMENT_UPDATE'
                              ? `/students/${app.student.source_ID}/update-documents/${app.id}`
                              : `/students/${app.student.source_ID}/update-info?mode=resubmit`
                          }
                          className="aegov-btn btn-xs"
                        >
                          {t.resubmit}
                        </a>
                      )}
                      {(app.status_ID === 1 || (app.applicationType === 'DOCUMENT_UPDATE' ? app.status_ID === 3 : app.status_ID === 4)) && (
                        <a href={`/students/${app.student.source_ID}`} className="aegov-btn btn-soft btn-xs">
                          {t.viewProfile}
                        </a>
                      )}
                    </div>
                  </div>

                  {/* Return Comment Banner */}
                  {app.status_ID === 2 && app.returnComment && (
                    <div className="mt-3 p-2 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 dark:bg-amber-950/40 dark:border-amber-800/60 dark:text-amber-300 text-sm flex items-start gap-2">
                      <WarningCircleIcon className="w-5 h-5 flex-shrink-0 text-amber-600 dark:text-amber-400" />
                      <div className="min-w-0 flex-1">
                        <span className="font-semibold me-1">{t.comment}:</span>
                        <span>{app.returnComment}</span>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
